import React from 'react';
import { Newspaper } from 'lucide-react';

/**
 * Press-clipping social-proof strip.
 *
 * Renders a thin, on-brand row of "As featured in" outlets right below
 * the stats row inside `FeaturesHeroCard`. Built to be honest about
 * provenance:
 *
 *   • When `OUTLETS` is populated → renders a tasteful greyscale row of
 *     publication names (and optional href + "Read the piece" links).
 *   • When `OUTLETS` is empty (no press yet) → renders a neutral
 *     "Press kit available" line with a mailto: link.
 *
 * IMPORTANT: do NOT add an outlet here unless lovinahome has actually
 * been featured in it. Fabricating press mentions is reputationally
 * fatal. The user populates this array as real coverage rolls in.
 */
const OUTLETS = [
  // Example shape — uncomment / replace with real entries once secured:
  // { name: 'The Irish Times', href: 'https://irishtimes.com/...', date: '2026-03' },
  // { name: 'Newstalk',        href: 'https://newstalk.com/...',   date: '2026-04' },
  // { name: 'The Journal',     href: 'https://thejournal.ie/...',  date: '2026-04' },
];

const PRESS_EMAIL = 'press@lovinahome.com';

export const PressStrip = ({ tone = 'dark' }) => {
  const onDark = tone === 'dark';
  const subdued = onDark ? 'text-white/60' : 'text-gray-500';
  const accent = onDark ? 'text-white/90' : 'text-gray-800';
  const divider = onDark ? 'bg-white/15' : 'bg-gray-300';

  if (!OUTLETS.length) {
    return (
      <div
        className={`mt-6 flex flex-wrap items-center gap-2 text-xs ${subdued}`}
        data-testid="press-strip-empty"
      >
        <Newspaper className="h-3.5 w-3.5" />
        <span>Journalist?</span>
        <a
          href={`mailto:${PRESS_EMAIL}?subject=lovinahome%20press%20kit`}
          className={`${accent} font-medium underline-offset-2 hover:underline`}
          data-testid="press-strip-mailto"
        >
          Grab our press kit
        </a>
        <span aria-hidden>·</span>
        <span>Logo, fact-sheet, founder bios, screenshots.</span>
      </div>
    );
  }

  return (
    <div
      className={`mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs ${subdued}`}
      data-testid="press-strip"
    >
      <span className="uppercase tracking-[0.16em] text-[10px] font-semibold flex items-center gap-1">
        <Newspaper className="h-3.5 w-3.5" /> As featured in
      </span>
      {OUTLETS.map((o, i) => (
        <React.Fragment key={o.name}>
          {o.href ? (
            <a
              href={o.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${accent} font-semibold tracking-tight hover:opacity-80 transition-opacity`}
              data-testid={`press-outlet-${i}`}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              {o.name}
            </a>
          ) : (
            <span
              className={`${accent} font-semibold tracking-tight`}
              data-testid={`press-outlet-${i}`}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              {o.name}
            </span>
          )}
          {i < OUTLETS.length - 1 && <span className={`h-3 w-px ${divider}`} aria-hidden />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default PressStrip;
